import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTrigger,
} from '@repo/shadcn';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { IwForm } from '@components/form';
import { DateInputField, TextField } from '@components/form/fields';

import { useCreateOpeningBalanceMutation } from '@/features/slice/balance-history/balance-history-api-slice';
import { toastError } from '@/utils';
import { formatGMT6 } from '@/utils/format-gmt6.util';

const OpeningBalanceFormSchema = z.object({
    date: z.coerce.string().min(1, 'Date is required'),
    starting_balance: z
        .preprocess(
            (value) => (value === '' ? undefined : value),
            z.coerce.number().min(0, 'Opening balance must be non-negative'),
        )
        .refine(
            (value) => Number.isFinite(value),
            'Opening balance is required',
        ),
});

type OpeningBalanceFormValues = z.infer<typeof OpeningBalanceFormSchema>;

const getTodayDate = () => new Date().toISOString().slice(0, 10);

//

export const AddOpeningBalanceDialog = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [createOpeningBalance, { isLoading }] =
        useCreateOpeningBalanceMutation();

    const initialValues: Partial<OpeningBalanceFormValues> = {
        date: getTodayDate(),
        starting_balance: undefined,
    };

    const handleSubmit = async (values: OpeningBalanceFormValues) => {
        try {
            await createOpeningBalance({
                date: formatGMT6(new Date(values.date)),
                starting_balance: values.starting_balance,
            }).unwrap();

            toast.success('Opening balance added');
            setIsOpen(false);
        } catch (error) {
            toastError(
                error,
                'AddOpeningBalanceDialog: Failed to add balance.',
            );
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button type={'button'}>Add History Balance</Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <h3 className={'text-lg font-bold'}>Add History Balance</h3>
                </DialogHeader>

                <IwForm
                    schema={OpeningBalanceFormSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit as any}
                    className={'mt-4 space-y-4'}
                >
                    <DateInputField<OpeningBalanceFormValues>
                        name={'date'}
                        label="Date"
                    />
                    {/*<TextField<OpeningBalanceFormValues>
                        name={'date'}
                        label={'Date'}
                        required={true}
                        inputProps={{ type: 'date' }}
                    />*/}

                    <TextField<OpeningBalanceFormValues>
                        name={'starting_balance'}
                        label={'Opening Balance'}
                        type={'number'}
                        required={true}
                        placeholder={'Enter opening balance'}
                        inputProps={{ step: '0.01', min: '0' }}
                    />

                    <div className={'flex justify-end gap-2'}>
                        <Button
                            type={'button'}
                            variant={'outline'}
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button type={'submit'} disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </IwForm>
            </DialogContent>
        </Dialog>
    );
};

AddOpeningBalanceDialog.displayName = 'AddOpeningBalanceDialog';
